import type { DialogButtonTemplateParams } from 'Components/smallComponents/dialogButton';
import type { RepeaterFunctionInvoker, TemplateFunction, TemplateResult } from 'Components/types';
import { garageCleanupRequested, vehicleDiscardingRequested } from 'Core/actions/fitmentSearch/index';
import { garageDataSelector } from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';

import type { VehicleListItemParams } from '../../types';
import VehicleListItem from '../../vehicleList/vehicleListItem';
import VehicleWidget from '../../vehicleWidget/index';
import type { VehicleWidgetTemplateParams } from '../../vehicleWidget/vehicleWidget';
import AddButton from '../addButton';
import { vehicleWidgetName } from '../garage';

interface Props {
  template: TemplateFunction<ListDropdownTemplateParams>;
  closeGarage: () => void;
  doNotRedirectOnVehicleSelect?: boolean;
  isAutoVehicleSelectionDisabled: boolean;
  isVehicleSelected: boolean;
  redirectUrl?: string;
  showVehicleDropdowns: () => void;
  useNativeDropdown?: boolean;
  useVehicleWidget?: boolean;
  vehicle: Record<string, string>;
}

export interface ListDropdownTemplateParams {
  addButton: (templ: TemplateFunction<DialogButtonTemplateParams>) => TemplateResult;
  clearGarage: () => void;
  closeGarage: () => void;
  vehicleWidget: (templ: TemplateFunction<VehicleWidgetTemplateParams>) => TemplateResult;
  items: RepeaterFunctionInvoker<VehicleListItemParams>;
  isVehicleSelected: boolean;
  showVehicleDropdowns: () => void;
  vehicle: Record<string, string>;
  discardSelectedVehicle: () => void;
  template: 'list';
}

export default function ListDropdown({
  template,
  closeGarage,
  doNotRedirectOnVehicleSelect,
  isAutoVehicleSelectionDisabled,
  isVehicleSelected,
  redirectUrl,
  showVehicleDropdowns,
  useNativeDropdown,
  useVehicleWidget,
  vehicle,
}: Props): TemplateResult {
  const dispatch = useDispatch();

  const garageData = useSelector(garageDataSelector);
  const garageItems: RepeaterFunctionInvoker<VehicleListItemParams> = garageData.map(
    (garageVehicle) => (itemTemplate) => (
      <VehicleListItem
        close={closeGarage}
        doNotRedirectOnVehicleSelect={doNotRedirectOnVehicleSelect}
        redirectUrl={redirectUrl}
        template={itemTemplate}
        vehicle={garageVehicle}
        key={garageVehicle.key}
      />
    ),
  );

  const clearGarage = () => {
    closeGarage();
    dispatch(garageCleanupRequested(isVehicleSelected));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const discardSelectedVehicle = () => {
    dispatch(vehicleDiscardingRequested());
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const addButton = (templ: TemplateFunction<DialogButtonTemplateParams>) => (
    <AddButton closeGarage={closeGarage} template={templ} key="add" />
  );

  const vehicleWidget = (templ: TemplateFunction<VehicleWidgetTemplateParams>) => {
    if (!useVehicleWidget) {
      return null;
    }

    const props = {
      isAlwaysActive: true,
      isAlwaysColumnLayout: true,
      isAutoVehicleSelectionDisabled,
      name: vehicleWidgetName,
      onSelectVehicle: closeGarage,
      doNotRedirectOnVehicleSelect,
      redirectUrl,
      useNativeDropdown,
      template: templ,
    };
    return <VehicleWidget {...props} key={vehicleWidgetName} />;
  };

  return template.call({
    addButton,
    clearGarage,
    closeGarage,
    vehicleWidget,
    items: garageItems,
    isVehicleSelected,
    showVehicleDropdowns,
    vehicle,
    discardSelectedVehicle,
    template: 'list',
  });
}
